import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectCreditCardIdFromRoute } from 'src/app/Account/redux/account.selectors'
import { selectPatchingOrganizationInviteFromState } from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { selectReceiptsAutoMatchEnabledFromState } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import ReceiptsAutoMatch from 'src/assets/svgs/Receipt-AutoMatch.svg'
import AccountHeader from 'src/components/AccountHeader/AccountHeader'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import { CreateNewDropdownOption } from 'src/components/CreateNewDropdown/CreateNewDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import Subnav, {
  SubnavDesign,
  SubnavOption,
} from 'src/components/Subnav/Subnav'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import Topnav, {
  TopnavAlternativeActionOptions,
} from 'src/components/Topnav/Topnav'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import { AvatarSizes, Issuers } from 'src/enums'
import { useBackgroundDispatch, useCookieState } from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { UserListModel } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import AccountAppRoute from 'src/navigation/appRoutes/account.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  accountBudgetsUrl,
  accountOverviewUrl,
  accountSettingsUrl,
  accountTransactionsUrl,
  accountVirtualCardsUrl,
  closeAccountUrl,
} from 'src/navigation/urls'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import { createSetIsUploadReceiptsAutoMatchPopupOpen } from 'src/redux/actions/app.actions'
import { patchCreditCardStatus } from 'src/redux/actions/data/creditCards.actions'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectLoadingFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const Account: React.FC = () => {
  const dispatch = useBackgroundDispatch()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)

  const { data: creditCard, isFetched } = useQueryCreditCard({
    creditCardId: creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCardId,
    retry: false,
  })

  const creditCardUser =
    creditCard?.user !== undefined
      ? new UserListModel(creditCard?.user)
      : undefined

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const extendLocation = useExtendLocation()

  const translate = useTranslate()

  const loadingRemoteConfig = useSelector(selectLoadingFromRemoteConfigState)
  const patchingOrganizationInvite = useSelector(
    selectPatchingOrganizationInviteFromState,
  )
  const receiptsAutoMatchEnabled = useSelector(
    selectReceiptsAutoMatchEnabledFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const currentUser = useSelector(selectUserListModelFromState)

  const { analytics } = useAnalyticsContext()

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard
  const canIssueVirtualCards = creditCardPermissions?.canIssueVirtualCards

  const { updateCookieValue: setShowAccountTutorialPromo } = useCookieState(
    CookieNames.Onboarding,
    CookieValuePaths.shouldShowAccountTutorialPromo,
    true,
  )

  const subnavOptions = useMemo<SubnavOption[]>(() => {
    if (!creditCard) return []

    const overview: SubnavOption = {
      label: <div>{translate('Overview')}</div>,
      to: accountOverviewUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountOverview,
      ariaLabelSource: 'Overview',
      'data-testid': 'overviewTab',
    }
    const budgets: SubnavOption = {
      label: <div>{translate('Budgets')}</div>,
      to: accountBudgetsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountBudgets,
      ariaLabelSource: 'Budgets',
      'data-testid': 'budgetsTab',
    }
    const virtualCards: SubnavOption = {
      label: <div>{translate('Virtual Cards')}</div>,
      to: accountVirtualCardsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountVirtualCards,
      ariaLabelSource: 'Virtual Cards',
      'data-testid': 'virtualCardsTab',
    }
    const transactions: SubnavOption = {
      label: <div>{translate('Transactions')}</div>,
      to: accountTransactionsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountTransactions,
      ariaLabelSource: 'Transactions',
      'data-testid': 'transactionsTab',
    }
    const accountSettings: SubnavOption = {
      label: <div>{translate('Account Settings')}</div>,
      to: accountSettingsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountSettings,
      ariaLabelSource: 'Account Settings',
      'data-testid': 'accountSettingsTab',
      setTooltip: canUpdateCreditCard ? setShowAccountTutorialPromo : undefined,
      tooltipTitle: <div>{translate('New!')}</div>,
      tooltipText: (
        <Trns
          source={
            '${bold} Reference Fields are now called Expense Categories. Manage them for all Accounts in your Organization under Organization Settings'
          }
          expressions={{
            bold: (
              <span
                css={css`
                  ${getFontCss(
                    12,
                    styleConstants.colors.white,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                <div>{translate('New!')}</div>
              </span>
            ),
          }}
        />
      ),
      sparkle: true,
    }

    const tabs: SubnavOption[] = []
    // TODO replace cardIsPhysicalOnly check with creditCard.features?.virtualCards when that is ready on BE
    const cardIsPhysicalOnly =
      creditCard.status === CreditCardStatus.NotApplicable
    // If the credit card is physical-only, we don't bother showing the overview tab
    if (!cardIsPhysicalOnly) {
      tabs.push(overview)
    }

    if (
      !cardIsPhysicalOnly &&
      creditCard.features?.budgets &&
      creditCard.isActive
    ) {
      tabs.push(budgets)
    }

    if ((!cardIsPhysicalOnly && creditCard.isActive) || creditCard.isClosed) {
      tabs.push(virtualCards)
    }
    if (creditCard.isActive || creditCard.isClosed) {
      tabs.push(transactions)
    }
    tabs.push(accountSettings)
    return tabs
  }, [
    creditCard?.id,
    creditCard?.status,
    creditCard?.physicalCard.status,
    creditCard?.features?.budgets,
    extendLocation.location,
    canUpdateCreditCard,
    setShowAccountTutorialPromo,
  ])

  const negativeActionOptions = useMemo<CreateNewDropdownOption[]>(() => {
    if (!creditCard || !canUpdateCreditCard || !creditCard.isActive) return []
    return [
      {
        label: <div>{translate('Close Account')}</div>,
        to: closeAccountUrl(creditCard.id),
        ariaLabelSource: translate('Close Account'),
        onClick: () => {
          analytics?.track('close account', {
            intent: 'open close account modal',
            interaction: 'button press',
            screen: accountOverviewUrl(':id'),
          })
        },
        image: <SvgTrash />,
        subtitle: (
          <p
            css={css`
              pointer-events: none;
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <div>
              {translate(
                'Permanently deactivate this account. All Virtual Cards & Budgets under this account will be closed.',
              )}
            </div>
          </p>
        ),
      },
    ]
  }, [
    creditCard?.id,
    creditCard?.isActive,
    canUpdateCreditCard,
    locale,
    analytics,
  ])

  const alternativeActionOptions = useMemo<
    TopnavAlternativeActionOptions[]
  >(() => {
    const opts: TopnavAlternativeActionOptions[] = [
      {
        title: <div>{translate('Account Options')}</div>,
        negativeActionOptions,
      },
    ]
    if (receiptsAutoMatchEnabled) {
      opts.push({
        title: <div>{translate('Auto-Match Receipts')}</div>,
        titleActions: {
          dispatch: () =>
            dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(true)),
          ariaLabelSource: 'Auto-Match Receipts',
          imageSrc: ReceiptsAutoMatch,
        },
      })
    }
    return opts
  }, [negativeActionOptions, receiptsAutoMatchEnabled, dispatch])

  const patchStatusInterval = useRef<NodeJS.Timeout | undefined>()
  useEffect(() => {
    if (creditCard?.issuer === Issuers.Amex) {
      if (creditCard?.isPending) {
        dispatch(patchCreditCardStatus(creditCardId))
      }
      patchStatusInterval.current = setInterval((): void => {
        if (creditCard?.isPending) {
          dispatch(patchCreditCardStatus(creditCardId))
        }
      }, 60000)
      return (): void => {
        if (patchStatusInterval.current !== undefined) {
          clearInterval(patchStatusInterval.current)
        }
      }
    }
  }, [creditCard?.id, creditCard?.status])

  if (!isFetched || loadingRemoteConfig || !!patchingOrganizationInvite) {
    return <LoadingBackgroundPage />
  }

  if (!creditCard) {
    return <NotFoundBackgroundPage />
  }

  return (
    <BackgroundPage>
      <Topnav
        titleElements={{
          title:
            creditCardUser?.id !== currentUser?.id &&
            creditCardUser !== undefined ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <UserAvatar
                  size={AvatarSizes.Small}
                  user={creditCardUser}
                  css={css`
                    margin-right: 10px;
                  `}
                />
                <Trns
                  source={"${firstName}'s Account"}
                  expressions={{
                    firstName: creditCardUser.firstNameOrEmail,
                  }}
                />
              </div>
            ) : (
              <div>{translate('Your Account')}</div>
            ),
          scrollContent: (
            <span
              css={css`
                font-size: 14px;
                line-height: 18px;
                ${!canIssueVirtualCards &&
                css`
                  margin-left: 32px;
                `}
              `}
            >
              {creditCard.truncatedDisplayName}
            </span>
          ),
        }}
        alternativeActionOptions={alternativeActionOptions}
      />
      <AccountHeader creditCard={creditCard} />
      <Subnav design={SubnavDesign.Header} options={subnavOptions} />
      <Switch location={calculatedBackgroundLocation}>
        {AccountAppRoute.subRoutes.map((route, i) => (
          <Route key={i} {...route.routeProps}>
            {route.component}
          </Route>
        ))}
        <Route {...AccountAppRoute.routeProps} exact>
          <Rdrct to={accountOverviewUrl(creditCard.id)} />
        </Route>
        <Route path={'/'}>
          <NotFoundBackgroundPageContent />
        </Route>
      </Switch>
    </BackgroundPage>
  )
}

export default Account
